import { QAOperationJson, QAOperationType } from '@scale/shared/types/qaOperation/qaOperation';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  BulkCreateQAOperationBody,
  BulkCreateQAOperationResponse,
  CreateQAOperationBody,
  CreateQAOperationResponse,
  GetAssignedQaOperationsResponse,
  GetOperationNodesResponse,
  TransitionAttemptReviewOperationResponse,
  UpdateQAOperationBody,
} from '@scale/shared/types/qaOperation/api';
import { IQAOperationFilterValues } from 'react-scale/components/remoAdmin/qaOperation/tables/tableProps';

/**
 * Creates a QA Operation
 *
 * Params and Context being separate generics is a bit risky because we don't actually guarantee that
 * they are correct. For example, in allStateMachineDefs we could change the state machine for an Operation Type,
 * but forget to update the Context in apiCreateOperation
 *
 * @param body
 */
export async function apiCreateOperation<Params, Context>(
  body: CreateQAOperationBody<Params, Context>,
) {
  return apiFetch<CreateQAOperationResponse>(null, '/corp-api/qm/operations', {
    method: 'POST',
    body,
  } as any);
}

export async function apiBulkCreateOperation<Params, Context>(
  body: BulkCreateQAOperationBody<Params, Context>,
) {
  return apiFetch<BulkCreateQAOperationResponse>(null, '/corp-api/qm/operations/batch', {
    method: 'POST',
    body,
  } as any);
}

export async function apiUpdateOperation(operationId: string, body: UpdateQAOperationBody) {
  return apiFetch<CreateQAOperationResponse>(null, `/corp-api/qm/operations/${operationId}`, {
    method: 'PUT',
    body,
  } as any);
}

export async function apiTransitionOperation<MachineEvent>(
  operationId: string,
  event: MachineEvent,
) {
  const opts = { method: 'POST', body: { event } };
  return apiFetch<TransitionAttemptReviewOperationResponse>(
    null,
    `/corp-api/qm/operations/${operationId}/transition`,
    opts,
  );
}

export async function getQaOperationNodes(qaOperationId: string) {
  return apiFetch<GetOperationNodesResponse>(
    null,
    `/corp-api/qm/operations/${qaOperationId}/nodes`,
  );
}

export async function getProjectOperationNodes(filters: IQAOperationFilterValues, page: number) {
  const queryFilters = getQueryFromFilters(filters);
  if (!queryFilters.project)
    throw new Error('Project ID missing from getProjectOperationNodes request');
  const query = { page, ...queryFilters };
  const opts = { query };
  return apiFetch<GetOperationNodesResponse>(null, `/corp-api/qm/project-operation-nodes`, opts);
}

export async function getCreatedQAOperations(
  page: number,
  filters: IQAOperationFilterValues = {},
  createdOnly?: boolean,
) {
  const query = { page, ...getQueryFromFilters(filters), createdOnly };
  return apiFetch<GetAssignedQaOperationsResponse>(null, '/corp-api/qm/operations', { query });
}

interface IOperationsOptions {
  fetchMetadata: boolean;
}

export async function getAssignedQAOperationNodes(
  filters: IQAOperationFilterValues,
  page: number,
  pendingOnly?: boolean,
  options?: IOperationsOptions,
) {
  const queryFilters = getQueryFromFilters(filters);
  const query = { onlyPending: !!pendingOnly, page, ...queryFilters, ...options };
  const opts = { query };
  return apiFetch<GetOperationNodesResponse>(null, '/corp-api/qm/assigned-operation-nodes', opts);
}

export async function getClaimedQAOperationNodes() {
  return apiFetch<GetOperationNodesResponse>(null, '/corp-api/qm/claimed-operation-nodes');
}

export async function getLatestAttemptResult(operationId: string) {
  return apiFetch(null, `/corp-api/qm/operations/${operationId}/latest-attempt-result`);
}

export async function findQAOperation(qaOperationId: string): Promise<QAOperationJson> {
  const { nodes } = await getQaOperationNodes(qaOperationId);
  if (!nodes || !nodes.length) throw new Error(`No nodes found for operation ID ${qaOperationId}`);
  return nodes[0].qaOperation;
}

export async function findQAOperationByDocument(
  operationType: QAOperationType,
  recordId: string,
): Promise<QAOperationJson> {
  const query = { operationType, recordId };
  const { qaOperation } = await apiFetch(null, `/corp-api/qm/operations/latest-active-related`, {
    query,
  });
  return qaOperation;
}

export async function validateQAOperationType(
  qaOperation: QAOperationJson,
  targetType: QAOperationType,
) {
  if (qaOperation.type !== targetType) {
    throw new Error(
      `QA Operation Type (${qaOperation.type}) does not match required type ${targetType} for operation ID ${qaOperation._id}`,
    );
  }
}

const getQueryFromFilters = (filters: IQAOperationFilterValues) => {
  const { sortOrder, priority, projectId } = filters;
  return {
    sortOrder: sortOrder ? -1 : 1,
    project: projectId,
    priority,
  };
};
